export const STANDARD_COLORS = [
  { name: 'Dark Red', value: '#C00000' },
  { name: 'Red', value: '#FF0000' },
  { name: 'Orange', value: '#FFC000' },
  { name: 'Yellow', value: '#FFFF00' },
  { name: 'Light Green', value: '#92D050' },
  { name: 'Green', value: '#00B050' },
  { name: 'Light Blue', value: '#00B0F0' },
  { name: 'Blue', value: '#0070C0' },
  { name: 'Dark Blue', value: '#002060' },
  { name: 'Purple', value: '#7030A0' }
];

/**
 * Variant ramps, as signed proportions: positive lightens towards white, negative darkens towards
 * black.
 */
const BACKGROUND_1_RAMP = [-0.05, -0.15, -0.25, -0.35, -0.5];
const TEXT_1_RAMP = [0.5, 0.35, 0.25, 0.15, 0.05];
const BACKGROUND_2_RAMP = [-0.1, -0.25, -0.5, -0.75, -0.9];
const ACCENT_RAMP = [0.8, 0.6, 0.4, -0.25, -0.5];

const THEME_COLOR_BASES = [
  { name: 'White, Background 1', value: '#FFFFFF', ramp: BACKGROUND_1_RAMP },
  { name: 'Black, Text 1', value: '#000000', ramp: TEXT_1_RAMP },
  { name: 'White, Background 2', value: '#E7E6E6', ramp: BACKGROUND_2_RAMP },
  { name: 'Black, Text 2', value: '#44546A', ramp: ACCENT_RAMP },
  { name: 'Blue, Accent 1', value: '#4472C4', ramp: ACCENT_RAMP },
  { name: 'Orange, Accent 2', value: '#ED7D31', ramp: ACCENT_RAMP },
  { name: 'Grey, Accent 3', value: '#A5A5A5', ramp: ACCENT_RAMP },
  { name: 'Gold, Accent 4', value: '#FFC000', ramp: ACCENT_RAMP },
  { name: 'Light Blue, Accent 5', value: '#5B9BD5', ramp: ACCENT_RAMP },
  { name: 'Green, Accent 6', value: '#70AD47', ramp: ACCENT_RAMP }
];

const toChannels = (hex) => [1, 3, 5].map((offset) => parseInt(hex.slice(offset, offset + 2), 16));

const toHex = (channels) =>
  channels
    .reduce(
      (hex, channel) =>
        hex +
        Math.min(255, Math.max(0, Math.round(channel)))
          .toString(16)
          .padStart(2, '0'),
      '#'
    )
    .toUpperCase();

const blend = (channel, proportion) =>
  proportion >= 0 ? channel + (255 - channel) * proportion : channel * (1 + proportion);

const rampLabel = (proportion) =>
  proportion >= 0
    ? `Lighter ${Math.round(proportion * 100)}%`
    : `Darker ${Math.round(-proportion * 100)}%`;

const variant = (base, proportion) => ({
  name: `${base.name}, ${rampLabel(proportion)}`,
  value: toHex(toChannels(base.value).map((channel) => blend(channel, proportion)))
});

export const THEME_COLOR_ROWS = [
  THEME_COLOR_BASES.map(({ name, value }) => ({ name, value })),
  ...ACCENT_RAMP.map((_, rowIndex) =>
    THEME_COLOR_BASES.map((base) => variant(base, base.ramp[rowIndex]))
  )
];

export const PALETTE_VALUES = [
  ...THEME_COLOR_ROWS.flat().map(({ value }) => value),
  ...STANDARD_COLORS.map(({ value }) => value)
];

const HEX_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

// Both the legacy comma form and the modern space-separated one, with an optional alpha channel
// (a number or a percentage, after a comma or a slash).
const RGB_PATTERN =
  /^rgba?\(\s*([\d.]+)\s*[,\s]\s*([\d.]+)\s*[,\s]\s*([\d.]+)(?:\s*[,/]\s*([\d.]+)(%?))?\s*\)$/i;

/**
 * Canonicalises a CSS colour to the `#RRGGBB` form the palette is written in, or null when it is
 * not a form we can convert (a CSS keyword such as `red`, or a translucent `rgba()`).
 *
 * A translucent colour is deliberately not converted: the palette is opaque, so flattening it would
 * change the colour the author applied. Callers keep the authored string instead.
 *
 * Needed because content is not always authored in the palette's own notation - pasted markup, the
 * `background` shorthand and anything a browser has already serialised through the CSSOM all arrive
 * as `rgb(255, 0, 0)` rather than `#FF0000`. Comparing those against a palette value directly never
 * matches, so the picker could not indicate the colour already applied. This is a display concern
 * only: what is stored stays exactly as it was authored.
 */
export const toPaletteHex = (value) => {
  if (!value) {
    return null;
  }

  const trimmed = String(value).trim();
  const hex = trimmed.match(HEX_PATTERN);

  if (hex) {
    const digits = hex[1];
    const pairs =
      digits.length === 3 ? [...digits].map((digit) => digit + digit) : digits.match(/../g);

    return `#${pairs.join('')}`.toUpperCase();
  }

  const rgb = trimmed.match(RGB_PATTERN);

  if (!rgb) {
    return null;
  }

  const [, red, green, blue, alpha, isPercentage] = rgb;
  const opacity = alpha === undefined ? 1 : Number(alpha) / (isPercentage ? 100 : 1);

  return opacity < 1 ? null : toHex([red, green, blue].map(Number));
};

export default THEME_COLOR_ROWS;
